import { useMemo, useState } from "react"
import { Alert, Box, Button, Collapse, IconButton, Stack } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { InfoButton } from "lib/@components/colored-buttons/info-button"
import { WarningButton } from "lib/@components/colored-buttons/warning-button"
import Iconify from "minimals-template/components/Iconify"
import { navigate } from "lib/routes/navigate"
import { formatMoney } from "../format-utils"
import { DataQualityAlertRow } from "./data-quality-alert-row"
import { getDataQualityDetail } from "./data-quality-detail-utils"

export function DataQualityAlerts({
    data,
    costResult,
    currencySymbol,
    regimeId,
    onNavigateToRateCard,
    onNavigateToSkillMapper,
    onRecalculate,
}) {
    const allAlerts = useMemo(buildAlerts, [
        data,
        costResult,
        currencySymbol,
        regimeId,
        onNavigateToRateCard,
        onNavigateToSkillMapper,
        onRecalculate,
    ])
    const [expanded, setExpanded] = useState(false)
    const [dismissedIds, setDismissedIds] = useState(createEmptySet)

    const visibleAlerts = useMemo(getVisibleAlerts, [allAlerts, dismissedIds])

    if (allAlerts.length === 0) return null

    if (visibleAlerts.length === 0) {
        const calculationLabel = getCalculationLabel()

        return (
            <Box>
                <Button
                    data-cy="data-quality-alerts-restore"
                    size="small"
                    variant="text"
                    color="inherit"
                    startIcon={<Iconify icon="mdi:alert-circle-outline" />}
                    onClick={handleRestore}
                    sx={{
                        color: "text.primary",
                        fontWeight: 500,
                        fontSize: "16px",
                        textTransform: "none",
                        px: 1,
                        "&:hover": { color: "text.primary", bgcolor: "action.hover" },
                    }}
                >
                    Show {allAlerts.length} calculation {calculationLabel}
                </Button>
            </Box>
        )
    }

    const warningCount = visibleAlerts.filter(isWarning).length
    const noteCount = visibleAlerts.length - warningCount
    const summarySeverity = warningCount > 0 ? "warning" : "info"
    const summaryIcon = getSummaryIcon()
    const toggleLabel = getToggleLabel()
    const toggleTransform = getToggleTransform()

    return (
        <Stack spacing={1}>
            <Alert
                severity={summarySeverity}
                icon={<Iconify icon={summaryIcon} />}
                onClick={handleToggle}
                sx={{
                    cursor: "pointer",
                    "& .MuiAlert-message": { flex: 1 },
                    "& .MuiAlert-action": { alignItems: "center", pt: 0, mr: 0 },
                }}
                action={
                    <IconButton
                        data-cy="data-quality-alerts-toggle"
                        size="small"
                        onClick={handleToggle}
                        aria-label={toggleLabel}
                    >
                        <Iconify
                            icon="mdi:chevron-down"
                            sx={{
                                transform: toggleTransform,
                                transition: "transform 0.2s",
                            }}
                        />
                    </IconButton>
                }
            >
                <Box component="span" sx={{ fontSize: 16 }}>
                    <strong>{buildSummaryLabel(warningCount, noteCount)}</strong> about this calculation
                </Box>
            </Alert>
            <Collapse in={expanded} unmountOnExit>
                <Stack spacing={1}>
                    <Repeat
                        list={visibleAlerts}
                        keyFn={getAlertKey}
                        item={
                            <DataQualityAlertRow
                                onDismiss={handleDismiss}
                                regimeId={regimeId}
                                currencySymbol={currencySymbol}
                                scenarioId={data?.id}
                                variantId={data?._variantId}
                                regimeName={data?.regimeName}
                            />
                        }
                    />
                </Stack>
            </Collapse>
        </Stack>
    )

    function handleToggle(event) {
        event?.stopPropagation?.()
        setExpanded((prev) => !prev)
    }

    function getSummaryIcon() {
        if (summarySeverity === "warning") return "mdi:alert-circle-outline"
        return "mdi:information-outline"
    }

    function getToggleLabel() {
        if (expanded) return "Hide notes"
        return "Show notes"
    }

    function getToggleTransform() {
        if (expanded) return "rotate(180deg)"
        return "rotate(0deg)"
    }

    function getCalculationLabel() {
        if (allAlerts.length === 1) return "note"
        return "notes"
    }

    function handleDismiss(id) {
        setDismissedIds((prev) => {
            const next = new Set(prev)
            next.add(id)
            return next
        })
    }

    function handleRestore() {
        setDismissedIds(new Set())
        setExpanded(true)
    }

    function renderRecalculateAction() {
        if (!onRecalculate) return null
        return <InfoButton size="small" sx={{ marginRight: 1 }} text="Recalculate" onClick={onRecalculate} />
    }

    function getVisibleAlerts() {
        return allAlerts.filter((alert) => !dismissedIds.has(alert.id))
    }

    function buildAlerts() {
        const list = []
        if (!costResult) return list
        const { transparency } = costResult

        // Scenario-level alerts (previously in ScenarioCostSummaryHeader)
        if (data?.rateCardIsArchived) {
            list.push({
                id: "rate-card-archived",
                severity: "warning",
                message:
                    "The assigned rate card has been archived. Costs may be outdated. Recalculating will still use this archived card, consider assigning a different rate card.",
                action: (
                    <WarningButton
                        size="small"
                        sx={{ marginRight: 1 }}
                        text="View Rate Card"
                        onClick={onNavigateToRateCard}
                    />
                ),
            })
        }
        if (isAllUnmappedScenario()) {
            list.push({
                id: "all-unmapped",
                severity: "warning",
                message: `${currencySymbol}0.00, all ${costResult?.taskCount} task(s) have unmapped skills. Map skills to trades in the Skill Mapper to calculate costs.`,
                action: (
                    <WarningButton
                        size="small"
                        sx={{ marginRight: 1 }}
                        text="Open Skill Mapper"
                        onClick={onNavigateToSkillMapper}
                    />
                ),
            })
        }
        if (data?.isStale) {
            list.push({
                id: "stale",
                severity: "info",
                // Not only edits: a schedule library release or a costing change can also make a result
                // out of date (FIQ-2457), so this does not name one cause
                message: `Something these costs depend on (such as the regime, rate card, skill map, rule set or schedule library) has changed since this scenario was last calculated${formatCalculatedDate()}. Recalculate to update costs.`,
                action: renderRecalculateAction(),
            })
        }

        // Calculation-level quality alerts
        if (costResult.phantomCostTasks?.count > 0) {
            list.push({
                id: "phantom-cost",
                severity: "warning",
                message: `${costResult.phantomCostTasks.count} task(s) with zero duration are incurring cost due to minimum billing periods. Total: ${currencySymbol}${formatMoney(costResult.phantomCostTasks.totalCost)}.`,
            })
        }
        if (costResult.unassignedSkills?.count > 0) {
            list.push({
                id: "unassigned-skills",
                severity: "warning",
                message: `${costResult.unassignedSkills.count} task(s) have no skill assigned and are excluded from costing (${costResult.unassignedSkills.hours} hours).`,
            })
        }
        if (costResult.allRatesZero) {
            list.push({
                id: "all-rates-zero",
                severity: "warning",
                message: "All trade rates are 0. Review the rate card.",
            })
        }
        if (!isAllUnmappedScenario() && costResult.unmappedSkills?.count > 0) {
            list.push({
                id: "unmapped-skills",
                severity: "warning",
                message: `${costResult.unmappedSkills.count} unmapped skill(s) (${costResult.unmappedSkills.hours} hours): ${costResult.unmappedSkills.skillNames?.join(", ")}`,
            })
        }
        if (!isAllUnmappedScenario() && costResult.unpricedTrades?.count > 0) {
            list.push({
                id: "unpriced-trades",
                severity: "warning",
                message: `${costResult.unpricedTrades.count} unpriced trade(s) (${costResult.unpricedTrades.hours} hours): ${costResult.unpricedTrades.tradeNames?.join(", ")}`,
            })
        }
        if (costResult.maintenanceSubtotal === 0 && costResult.grandTotal > 0) {
            list.push({
                id: "zero-trade-costs",
                severity: "warning",
                message: `Trade costs are ${currencySymbol}0.00 but the total includes fixed additional items.`,
            })
        }
        if (transparency?.defaultRateFallback?.count > 0) {
            list.push({
                id: "default-rate-fallback",
                severity: "info",
                message: `${transparency.defaultRateFallback.count} trade(s) used the default rate as no specific rate was defined: ${transparency.defaultRateFallback.tradeNames?.join(", ")}.`,
            })
        }
        if (hasMinBillingInflation()) {
            list.push({
                id: "min-billing-inflation",
                severity: "info",
                message: `Minimum billing periods increased billed hours by ${getInflationPercent()}% (from ${transparency.minimumBillingInflation.originalHours} to ${transparency.minimumBillingInflation.billedHours} hours across ${transparency.minimumBillingInflation.affectedCount} tasks).`,
            })
        }
        if (transparency?.skippedNoFrequencyCount > 0) {
            list.push({
                id: "no-frequency",
                severity: "info",
                message: `${transparency.skippedNoFrequencyCount} task(s) have no valid frequency and are excluded from costing.`,
                action: (
                    <If value={regimeId}>
                        <InfoButton
                            size="small"
                            sx={{ marginRight: 1 }}
                            text="View Tasks"
                            onClick={handleNavigateToUnspecifiedFrequencies}
                        />
                    </If>
                ),
            })
        }
        if (transparency?.zeroDurationCount > 0) {
            list.push({
                id: "zero-duration",
                severity: "info",
                message: `${transparency.zeroDurationCount} task(s) have zero duration and contribute no cost.`,
            })
        }
        if (getUnassignedFacilityPercent() > 25) {
            list.push({
                id: "unassigned-facility",
                severity: "info",
                message: `${getUnassignedFacilityPercent()}% of costed tasks are in the "Unassigned" facility bucket.`,
            })
        }
        // FIQ-1658: attach the per-task detail behind each warning (null for alert ids
        // without one, and for results calculated before dataQualityDetails existed).
        for (const alert of list) {
            alert.detail = getDataQualityDetail(costResult, alert.id)
        }
        return list
    }

    function isAllUnmappedScenario() {
        return (
            costResult &&
            costResult.taskCount > 0 &&
            costResult.costedTaskCount === 0 &&
            costResult.unmappedSkills?.count > 0
        )
    }

    function hasMinBillingInflation() {
        return costResult.transparency?.minimumBillingInflation?.affectedCount > 0
    }

    function getInflationPercent() {
        const orig = costResult.transparency?.minimumBillingInflation?.originalHours || 0
        const billed = costResult.transparency?.minimumBillingInflation?.billedHours || 0
        if (orig === 0) return billed > 0 ? 100 : 0
        return Math.round(((billed - orig) / orig) * 100)
    }

    function formatCalculatedDate() {
        if (!data?.calculatedAt) return ""
        const d = new Date(data.calculatedAt)
        if (isNaN(d.getTime())) return ""
        return ` on ${d.toLocaleDateString("en-GB")}`
    }

    function handleNavigateToUnspecifiedFrequencies() {
        const groupId = regimeId?.replace(/^(live|ready)\|/, "")?.split(":")?.[0]
        if (groupId) navigate(`/app/schedules?id=${groupId}&regime_assetsTab=zero-u-wizard`)
    }

    function getUnassignedFacilityPercent() {
        const unassigned = costResult.facilityBreakdown?.find(isUnassignedFacility)
        if (!unassigned || !costResult.maintenanceSubtotal) return 0
        return Math.round((unassigned.totalCost / costResult.maintenanceSubtotal) * 100)
    }
}

function createEmptySet() {
    return new Set()
}

function isUnassignedFacility(f) {
    return f.facilityId === "unassigned"
}

function getAlertKey(alert) {
    return alert.id
}

function isWarning(alert) {
    return alert.severity === "warning"
}

function buildSummaryLabel(warningCount, noteCount) {
    const parts = []
    if (warningCount > 0) parts.push(`${warningCount} ${warningCount === 1 ? "warning" : "warnings"}`)
    if (noteCount > 0) parts.push(`${noteCount} ${noteCount === 1 ? "note" : "notes"}`)
    return parts.join(" · ")
}
